import React from "react";
import Select, { createFilter } from "react-select";

/**
 * A searchable replacement for a native <select>.
 *
 * - Value in and out is the option's plain value (like <select>), so it drops in where a <select> was.
 * - The menu is rendered in <body> and scrolls on its own, so a parent that clips or cannot scroll never hides options.
 * - Typing filters by label, so long lists stay usable.
 * - The look is driven by CSS variables so a form can match its native inputs. Set them on any ancestor:
 *   --aso-height, --aso-radius, --aso-border, --aso-focus-border, --aso-color, --aso-background,
 *   --aso-font-size, --aso-line-height, --aso-padding-x. Defaults suit a standard Bootstrap form.
 *   The menu is outside the form in the DOM, so it keeps the defaults.
 */

const MENU_MAX_HEIGHT = 240;
const MENU_LAYER = 2000; // above react-bootstrap modals (1055)

const controlBorder = (focused) => (focused ? "var(--aso-focus-border, #0b84fe)" : "var(--aso-border, #ced4da)");

const styles = {
  control: (base, state) => ({
    ...base,
    height: "var(--aso-height, 40px)",
    minHeight: "var(--aso-height, 40px)",
    borderRadius: "var(--aso-radius, 6px)",
    borderColor: controlBorder(state.isFocused),
    backgroundColor: state.isDisabled ? "#e9ecef" : "var(--aso-background, transparent)",
    boxShadow: "none",
    cursor: "pointer",
    "&:hover": { borderColor: controlBorder(state.isFocused) },
  }),
  valueContainer: (base) => ({ ...base, height: "100%", padding: "0 var(--aso-padding-x, 12px)" }),
  singleValue: (base) => ({
    ...base,
    margin: 0,
    color: "var(--aso-color, #212529)",
    fontSize: "var(--aso-font-size, 14px)",
    lineHeight: "var(--aso-line-height, 1.5)",
  }),
  placeholder: (base) => ({
    ...base,
    margin: 0,
    color: "var(--aso-color, #6c757d)",
    fontSize: "var(--aso-font-size, 14px)",
    lineHeight: "var(--aso-line-height, 1.5)",
  }),
  input: (base) => ({
    ...base,
    margin: 0,
    padding: 0,
    color: "var(--aso-color, #212529)",
    fontSize: "var(--aso-font-size, 14px)",
    lineHeight: "var(--aso-line-height, 1.5)",
  }),
  indicatorSeparator: () => ({ display: "none" }),
  dropdownIndicator: (base) => ({
    ...base,
    color: "var(--aso-color, #6c757d)",
    padding: "0 var(--aso-padding-x, 12px) 0 0",
    "&:hover": { color: "var(--aso-color, #6c757d)" },
  }),
  menuPortal: (base) => ({ ...base, zIndex: MENU_LAYER }),
  menu: (base) => ({ ...base, borderRadius: 8, overflow: "hidden", marginTop: 4 }),
  option: (base, state) => ({
    ...base,
    cursor: "pointer",
    color: state.isSelected ? "#fff" : "#212529",
    backgroundColor: state.isSelected ? "#0253a5" : state.isFocused ? "#f1f5fd" : "transparent",
    ":active": { backgroundColor: state.isSelected ? "#0253a5" : "#e2ebf9" },
  }),
};

// Scrolling the page closes a fixed-position menu so it never floats away from its field;
// scrolling the menu's own list must not.
const closeMenuOnPageScroll = (event) =>
  !(event.target instanceof Element && event.target.closest(".aso__menu"));

const AutocompleteSelect = ({
  options,
  value,
  onChange,
  getOptionValue = (option) => String(option.value),
  getOptionLabel = (option) => option.label,
  placeholder = "Select...",
  emptyMessage = "No options available",
  noMatchMessage = "No matching options",
  inputId,
  name,
  isDisabled = false,
  showIndicator = true,
}) => {
  const optionValue = (option) => String(getOptionValue(option));
  const optionLabel = (option) => String(getOptionLabel(option) ?? "");
  const selected = options.find((option) => optionValue(option) === String(value ?? "")) ?? null;

  return (
    <Select
      classNamePrefix="aso"
      inputId={inputId}
      name={name}
      options={options}
      value={selected}
      onChange={(option) => onChange(option ? optionValue(option) : "", option)}
      getOptionValue={optionValue}
      getOptionLabel={optionLabel}
      // stringify receives react-select's { label, value, data } wrapper, whose label is already getOptionLabel's.
      filterOption={createFilter({ ignoreCase: true, ignoreAccents: true, matchFrom: "any", stringify: (option) => option.label })}
      placeholder={placeholder}
      noOptionsMessage={({ inputValue }) => (inputValue ? noMatchMessage : emptyMessage)}
      isDisabled={isDisabled}
      isSearchable
      components={showIndicator ? undefined : { DropdownIndicator: null }}
      styles={styles}
      maxMenuHeight={MENU_MAX_HEIGHT}
      menuPlacement="auto"
      menuPosition="fixed"
      menuPortalTarget={typeof document === "undefined" ? null : document.body}
      closeMenuOnScroll={closeMenuOnPageScroll}
    />
  );
};

export default AutocompleteSelect;
